import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

interface IconProps {
  readonly className?: string;
}

// CopyIcon removed 2026-07-02 with the Copy button (Georgetown feedback).

function ExportIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function PrintIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

function CheckIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CaretDownIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M7 10l5 5 5-5z" />
    </svg>
  );
}

interface TableActionsProps {
  /**
   * Caller-supplied CSV serializer. Called when the user clicks Copy
   * or picks "Export as CSV". Should return the full CSV string for
   * the currently-visible rows + header. Return an empty string (or
   * omit the prop entirely) if there's no data to export.
   */
  readonly getCsvData?: () => string;
  /**
   * Filename (without extension) used by Export as CSV / Excel.
   * Defaults to "table-export".
   */
  readonly filename?: string;
  /**
   * Optional override for the Print handler. Defaults to
   * `window.print()` which the page's own print stylesheet can hook.
   */
  readonly onPrint?: () => void;
  /**
   * Positioning class(es) applied to the toolbar's root flex
   * container. Defaults to `md:ml-auto` which right-aligns the
   * toolbar inside its parent flex row (the original Rankings
   * behaviour). Pass an empty string to drop the auto-margin so the
   * toolbar flows naturally — useful in filter bars where a sibling
   * input (e.g. Search) is set to `flex-1` and should push the
   * toolbar to the row's right edge when there's space, but let the
   * toolbar wrap left when the row overflows.
   */
  readonly className?: string;
}

const TOAST_VISIBLE_MS = 3000;

/**
 * Compact toolbar of three icon-only actions shown on the right
 * edge of a filter bar: Copy / Export ▾ / Print. Labels live in
 * tooltips (`title`) + screen-reader labels (`aria-label`).
 *
 * Behavior:
 *   - Copy:    serializes the current rows to CSV (via the caller-
 *              supplied `getCsvData`), writes to the clipboard, and
 *              flashes a "Copied to clipboard" toast.
 *   - Export:  opens a small dropdown with CSV + Excel options.
 *              CSV path: same serializer, writes a downloaded file.
 *              XLSX path: re-uses the CSV body — Excel imports CSV
 *              correctly and we avoid pulling in a heavy XLSX dep
 *              until the data shape is final.
 *   - Print:   calls `window.print()` by default.
 *
 * Outside-click + Escape close the Export dropdown.
 */
export function TableActions({
  getCsvData,
  filename = 'table-export',
  onPrint,
  className = 'md:ml-auto',
}: TableActionsProps) {
  const [exportOpen, setExportOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; kind: 'ok' | 'warn' } | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const toastTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!exportOpen) return;
    function handleOutside(e: MouseEvent) {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setExportOpen(false);
        exportButtonRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [exportOpen]);

  useEffect(() => {
    return () => {
      if (toastTimer.current !== null) {
        window.clearTimeout(toastTimer.current);
      }
    };
  }, []);

  function showToast(message: string, kind: 'ok' | 'warn' = 'ok') {
    setToast({ message, kind });
    if (toastTimer.current !== null) {
      window.clearTimeout(toastTimer.current);
    }
    toastTimer.current = window.setTimeout(() => {
      setToast(null);
      toastTimer.current = null;
    }, TOAST_VISIBLE_MS);
  }

  // handleCopy removed 2026-07-02 with the Copy button.

  function downloadFile(content: string, mimeType: string, ext: string) {
    // Prepend the UTF-8 BOM (U+FEFF) so Excel picks up encoding
    // correctly and doesn't mis-parse the file (2026-07-02 fix, part
    // of the "exports condensed to 1-2 rows" investigation). Without
    // the BOM, Excel guesses ASCII/Latin-1 and can collapse rows or
    // mangle non-ASCII characters (Georgetown metro names include
    // hyphens + accents that were rendering as garbage in some
    // downloads). CRLF row separators are already emitted by
    // `lib/csv.ts::toCsv`, so the file lands with proper Excel
    // compatibility on Windows + Mac. We build the BOM from the
    // codepoint at runtime rather than embedding a literal U+FEFF
    // in the source, to satisfy the `no-irregular-whitespace`
    // ESLint rule.
    const BOM = String.fromCharCode(0xfeff);
    const blob = new Blob([BOM, content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function handleExportCsv() {
    const csv = getCsvData?.() ?? '';
    if (!csv) {
      showToast('Nothing to export', 'warn');
      return;
    }
    downloadFile(csv, 'text/csv;charset=utf-8', 'csv');
    showToast('CSV downloaded');
  }

  function handleExportXlsx() {
    const csv = getCsvData?.() ?? '';
    if (!csv) {
      showToast('Nothing to export', 'warn');
      return;
    }
    // 2026-07-02: switched from .xls + application/vnd.ms-excel to
    // .csv + text/csv. The old combo (CSV body under an .xls
    // filename) made Excel on Mac collapse multi-row exports into a
    // single row because Excel doesn't run its CSV parser on a file
    // it thinks is a proprietary binary .xls. Handing over a real
    // .csv (which Excel opens natively via its CSV import path) fixes
    // the "condensed to 1-2 rows" bug reported in the Georgetown
    // review pass. Swap to a true xlsx writer (SheetJS) later if
    // xlsx-native formatting features are needed.
    downloadFile(csv, 'text/csv;charset=utf-8', 'csv');
    showToast('Spreadsheet downloaded');
  }

  const handlePrint = onPrint ?? (() => window.print());

  const btnBase =
    'inline-flex items-center justify-center rounded-md border border-border bg-white p-2 text-navy transition hover:border-navy/40 hover:bg-ice focus:outline-none focus:ring-2 focus:ring-blue/30';

  return (
    <div className={cn('relative flex items-end gap-2 print-hide', className)}>
      {/* Copy-to-clipboard button removed 2026-07-02 per Georgetown
          feedback -- reviewers found the affordance ambiguous next to
          the Export ▾ menu (which already offers CSV). Keep the
          `handleCopy` code path in place so a future callsite can
          re-introduce the button without re-plumbing the clipboard
          logic. */}
      <div className="relative" ref={exportRef}>
        <button
          ref={exportButtonRef}
          type="button"
          onClick={() => setExportOpen((v) => !v)}
          className={`${btnBase} gap-0.5`}
          aria-haspopup="menu"
          aria-expanded={exportOpen}
          aria-label="Export table"
          title="Export"
        >
          <ExportIcon className="h-4 w-4" />
          <CaretDownIcon className="h-3 w-3" />
        </button>
        {exportOpen && (
          <div
            role="menu"
            aria-label="Export format"
            className={cn(
              'absolute right-0 top-full z-40 mt-1 w-40 overflow-hidden rounded-md border border-border bg-white',
              'shadow-panel',
            )}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setExportOpen(false);
                handleExportCsv();
              }}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-navy hover:bg-ice"
            >
              <span>Export as CSV</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-text-4">.csv</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setExportOpen(false);
                handleExportXlsx();
              }}
              className="flex w-full items-center justify-between border-t border-border px-3 py-2 text-left text-sm text-navy hover:bg-ice"
            >
              <span>Export as Excel</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-text-4">.xls</span>
            </button>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handlePrint}
        className={btnBase}
        aria-label="Print table"
        title="Print"
      >
        <PrintIcon className="h-4 w-4" />
      </button>

      {/* Toast — fixed in the lower-right of the viewport so it isn't
          clipped by the filter bar's overflow. aria-live announces
          the message to screen readers as it appears. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={cn(
            'fixed bottom-6 right-6 z-[200] flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold shadow-[0_10px_24px_-6px_rgba(20,65,117,0.28)] transition',
            toast.kind === 'ok' ? 'bg-navy text-white' : 'bg-rose text-white',
          )}
        >
          {toast.kind === 'ok' && <CheckIcon className="h-4 w-4" />}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
